/** 薪酬阶段3 · 个人激励卡片（挂工作台 sales 个人视图）。
 *  两条进度条(业绩/单量到下一级阈值) + 升一级能多拿(底薪涨幅+5班型每单提成增量) + 本月提成【预估】。 */
import { useQuery } from '@tanstack/react-query';
import { $ } from '../lib/api';
import './viz/incentive.css';

const yuan = (n: number) => {
  const v = Number(n || 0);
  return v >= 10000 ? `¥${(v / 10000).toFixed(v % 10000 === 0 ? 0 : 1)}万` : `¥${Math.round(v).toLocaleString()}`;
};

// 等级中文 → 徽章配色类（见习=灰/初级=蓝/中级=紫/高级=金渐变）
const gradeClass = (label: string) => {
  if (label === '高级') return 'inc-badge--senior';
  if (label === '中级') return 'inc-badge--mid';
  if (label === '初级') return 'inc-badge--junior';
  return 'inc-badge--trainee'; // 见习 + 兜底
};
const gradeIcon = (label: string) => {
  if (label === '高级') return '👑';
  if (label === '中级') return '🔷';
  if (label === '初级') return '🔹';
  return '🌱';
};
const clampPct = (n: number) => Math.max(0, Math.min(100, Math.round(Number(n) || 0)));

// 单条现代进度条（粗圆角 + 渐变填充 + 当前/目标标注 + 接近完成转绿）
function IncProgressBar({ name, currentText, targetText, pct }: { name: string; currentText: string; targetText: string; pct: number }) {
  const p = clampPct(pct);
  const near = p >= 80;
  return (
    <div className="inc-prog">
      <div className="inc-prog-meta">
        <span className="inc-prog-name">{name}</span>
        <span className="inc-prog-vals">
          <b>{currentText}</b> / {targetText}
          <span className="inc-prog-pct" style={{ color: near ? 'var(--success-text)' : 'var(--primary)' }}>{p}%</span>
        </span>
      </div>
      <div className="inc-prog-track">
        <div className={`inc-prog-fill ${near ? 'inc-prog-fill--green' : 'inc-prog-fill--blue'}`} style={{ ['--inc-pct' as any]: `${p}%` }} />
      </div>
    </div>
  );
}

export function MyIncentiveCard() {
  const { data } = useQuery<any>({
    queryKey: ['my-incentive'],
    queryFn: () => $.get('/salary/my-incentive'),
    refetchInterval: 120_000,
  });
  if (!data) return null;
  const { gradeLabel, baseSalary, progress, upgradeGain, estCommissionThisMonth, isTop, maintain } = data;
  const bonusDeltas = (upgradeGain?.bonusDeltas ?? []).filter((b: any) => Number(b.delta) !== 0);

  const perfGap = progress ? Math.max(0, (progress.perf.target ?? 0) - progress.perf.current) : 0;
  const dealGap = progress ? Math.max(0, (progress.deals.target ?? 0) - progress.deals.current) : 0;

  return (
    <div className="inc-card">
      {/* 头部：标题 + 等级徽章 + 底薪 + 本月提成大数字 */}
      <div className="inc-head">
        <span className="inc-title">💰 我的激励</span>
        <span className={`inc-badge ${gradeClass(gradeLabel)}${gradeLabel === '高级' ? ' inc-badge-shine' : ''}`}>
          <span className="inc-badge-ico">{gradeIcon(gradeLabel)}</span>
          {gradeLabel}
        </span>
        <span className="inc-base">底薪 {yuan(baseSalary)}/月</span>
        <span className="inc-commission">
          <span className="inc-commission-label">本月提成 <span className="inc-commission-tag">预估</span></span>
          <span className="inc-commission-num">{yuan(estCommissionThisMonth)}</span>
        </span>
      </div>

      {isTop ? (
        <div className="inc-top">
          <span className="inc-top-trophy">🏆</span>
          <span>已是最高职级</span>
          {maintain && (
            <span className="inc-top-maintain">
              保持 月业绩 ≥{yuan(maintain.perf)} 或 单量 ≥{maintain.deals} 单 即稳级
            </span>
          )}
        </div>
      ) : progress ? (
        <>
          {/* 升级紧迫：还差 ¥X / N 单 + gate 约束 */}
          <div className="inc-gap-line">
            升「{upgradeGain?.nextGradeLabel}」还差
            <b className="inc-gap-strong">{yuan(perfGap)}</b>
            <span className="inc-gap-or">业绩</span>
            <span className="inc-gap-or">或</span>
            <b className="inc-gap-strong">{dealGap}</b>
            <span className="inc-gap-or">单</span>
            {progress.gate && (
              <span className={`inc-gate ${progress.gate.ok ? 'inc-gate--ok' : 'inc-gate--pending'}`}>
                孵化≥{progress.gate.requireIncub} 或 创业≥{progress.gate.requireEntre}（当前 {progress.gate.incub}/{progress.gate.entre}）{progress.gate.ok ? ' ✓' : ''}
              </span>
            )}
          </div>

          {/* 现代进度条：业绩 / 单量 */}
          <IncProgressBar
            name="业绩(含启航)"
            currentText={yuan(progress.perf.current)}
            targetText={yuan(progress.perf.target ?? 0)}
            pct={progress.perf.pct}
          />
          <IncProgressBar
            name="单量(去启航)"
            currentText={`${progress.deals.current} 单`}
            targetText={progress.deals.target != null ? `${progress.deals.target} 单` : '—'}
            pct={progress.deals.pct}
          />

          {/* 升级路径可视化：当前级 → 下一级 + 收益对照 */}
          <div className="inc-upgrade">
            <div className="inc-ladder">
              <span className={`inc-badge ${gradeClass(gradeLabel)}`}>
                <span className="inc-badge-ico">{gradeIcon(gradeLabel)}</span>{gradeLabel}
              </span>
              <span className="inc-ladder-arrow">→</span>
              <span className={`inc-badge ${gradeClass(upgradeGain?.nextGradeLabel ?? '')}${upgradeGain?.nextGradeLabel === '高级' ? ' inc-badge-shine' : ''}`}>
                <span className="inc-badge-ico">{gradeIcon(upgradeGain?.nextGradeLabel ?? '')}</span>{upgradeGain?.nextGradeLabel}
              </span>
              <span className="inc-ladder-hint">升一级·每月多拿 👇</span>
            </div>
            <div className="inc-gains">
              <div className="inc-gain inc-gain--base">
                <span className="inc-gain-label">底薪/月</span>
                <span className="inc-gain-delta">+{yuan(upgradeGain?.baseDelta ?? 0)}</span>
              </div>
              {bonusDeltas.map((b: any) => (
                <div className="inc-gain" key={b.label}>
                  <span className="inc-gain-label">{b.label}·每单</span>
                  <span className="inc-gain-delta">+{yuan(b.delta)}</span>
                </div>
              ))}
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}
